<template>
  <div class="highlight-benefits-modal-body">
    <h2 class="highlight-benefits-modal__title">{{ title }}</h2>

    <p v-if="dealName" class="highlight-benefits-modal__deal-name">„{{ dealName }}"</p>
    <p class="highlight-benefits-modal__intro">{{ HIGHLIGHT_PURCHASE_INTRO }}</p>

    <div class="highlight-benefits-modal__balance">
      <span class="highlight-benefits-modal__balance-label">{{ HIGHLIGHT_POINTS_BALANCE_LABEL }}</span>
      <span class="highlight-benefits-modal__balance-value">{{ formattedBalance }}</span>
    </div>

    <div class="highlight-benefits-modal__actions-stack">
      <button
        v-if="canPayWithPoints"
        type="button"
        class="highlight-benefits-modal__pill highlight-benefits-modal__pill--primary"
        @click="$emit('confirm-points')"
      >
        {{ pointsRedeemLabel }}
      </button>
      <div
        v-else-if="pointsCost > 0"
        class="highlight-benefits-modal__pill highlight-benefits-modal__pill--disabled info-tooltip-wrapper"
      >
        <span>{{ pointsCollectLabel }}</span>
        <button
          type="button"
          class="info-btn"
          aria-label="Informationen zu Punkten"
          @click.stop="$emit('toggle-tooltip')"
        >
          <ion-icon :src="infoIcon" />
        </button>
      </div>

      <transition name="info-fade">
        <div
          v-if="showTooltip && !canPayWithPoints"
          class="card-info-tooltip card-info-tooltip--modal-inline"
          @click.stop
        >
          <p class="card-info-tooltip__header">{{ PARTNER_POINTS_TOOLTIP_HEADER }}</p>
          <template v-if="tooltipLines.earnItems.length">
            <p class="card-info-tooltip__section">{{ PARTNER_POINTS_TOOLTIP_EARN_SECTION }}</p>
            <ul class="card-info-tooltip__list">
              <li v-for="item in tooltipLines.earnItems" :key="item">
                <span v-html="formatTooltipItem(item)" />
              </li>
            </ul>
          </template>
          <template v-if="tooltipLines.purchaseItems.length">
            <p class="card-info-tooltip__section">{{ PARTNER_POINTS_TOOLTIP_PURCHASE_SECTION }}</p>
            <ul class="card-info-tooltip__list">
              <li v-for="item in tooltipLines.purchaseItems" :key="item">
                <span v-html="formatTooltipItem(item)" />
              </li>
            </ul>
          </template>
          <p class="card-info-tooltip__footnote">{{ PARTNER_POINTS_TOOLTIP_FOOTNOTE }}</p>
        </div>
      </transition>

      <button
        type="button"
        class="highlight-benefits-modal__pill"
        :class="canPayWithPoints
          ? 'highlight-benefits-modal__pill--secondary'
          : 'highlight-benefits-modal__pill--primary'"
        @click="$emit('confirm-stripe')"
      >
        {{ priceButtonLabel }}
      </button>
    </div>

    <div class="highlight-benefits-modal__footer">
      <button
        type="button"
        class="highlight-benefits-modal__footer-action highlight-benefits-modal__footer-action--secondary"
        @click="$emit('dismiss')"
      >
        Abbrechen
      </button>
      <button
        type="button"
        class="highlight-benefits-modal__footer-action"
        @click="$emit('footer-primary')"
      >
        {{ footerPrimaryLabel }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { IonIcon } from '@ionic/vue';
import infoIcon from '@/assets/icons_v2/info_full_icon.svg';
import {
  HIGHLIGHT_PURCHASE_INTRO,
  HIGHLIGHT_POINTS_BALANCE_LABEL,
  PARTNER_POINTS_TOOLTIP_HEADER,
  PARTNER_POINTS_TOOLTIP_EARN_SECTION,
  PARTNER_POINTS_TOOLTIP_PURCHASE_SECTION,
  PARTNER_POINTS_TOOLTIP_FOOTNOTE,
} from '@/components/partner/partnerMembershipCopy.js';

defineProps({
  title: { type: String, required: true },
  dealName: { type: String, default: '' },
  formattedBalance: { type: String, required: true },
  canPayWithPoints: { type: Boolean, default: false },
  pointsCost: { type: Number, default: 0 },
  pointsRedeemLabel: { type: String, required: true },
  pointsCollectLabel: { type: String, required: true },
  priceButtonLabel: { type: String, required: true },
  footerPrimaryLabel: { type: String, required: true },
  showTooltip: { type: Boolean, default: false },
  tooltipLines: { type: Object, required: true },
  formatTooltipItem: { type: Function, required: true },
});

defineEmits([
  'dismiss',
  'confirm-points',
  'confirm-stripe',
  'footer-primary',
  'toggle-tooltip',
]);
</script>

<style lang="scss" scoped>
.highlight-benefits-modal__title {
  margin: 0 0 10px;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  text-align: left;
  color: #1a1a1a;
}

.highlight-benefits-modal__deal-name {
  margin: 0 0 8px;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  color: #1a1a1a;
}

.highlight-benefits-modal__intro {
  margin: 0 0 14px;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  color: #1a1a1a;
}

.highlight-benefits-modal__balance {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 6px;
  margin-bottom: 16px;
}

.highlight-benefits-modal__balance-label {
  font-size: 0.8125rem;
  color: #1a1a1a;
}

.highlight-benefits-modal__balance-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #ffcc00;
  letter-spacing: 0.02em;
}

.highlight-benefits-modal__actions-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}

.highlight-benefits-modal__pill {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  border: none;
  border-radius: 999px;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
}

.highlight-benefits-modal__pill--primary {
  background: #111;
  color: #fff;
}

.highlight-benefits-modal__pill--secondary {
  background: #f0f0f0;
  color: #111;
}

.highlight-benefits-modal__pill--disabled {
  background: #f0f0f0;
  color: #111;
  cursor: default;
}

.info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ion-icon {
    font-size: 20px;
    color: #1a1a1a;
  }
}

.highlight-benefits-modal__footer {
  display: flex;
  border-top: 1px solid #ebebeb;
  margin: 0 -20px;
}

.highlight-benefits-modal__footer-action {
  flex: 1;
  display: block;
  width: 100%;
  margin: 0;
  padding: 15px 8px;
  border: none;
  background: transparent;
  color: #3b99fc;
  font-size: 0.95rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-align: center;
  cursor: pointer;
}

.highlight-benefits-modal__footer-action:not(:last-child) {
  border-right: 1px solid #ebebeb;
}

.highlight-benefits-modal__footer-action--secondary {
  color: #999;
  font-weight: 400;
}
</style>

<style lang="scss">
@use '@/styles/card-info-tooltip.scss';
</style>
